import { z } from 'zod';
import { isAlwaysDrawnSlot, isVisibleSlot } from '../rules/appearance.js';
import { AffixListSchema } from './affix-ids.js';
import { ALCHEMY_POTION_TEMPLATES } from './alchemy.js';
import { QUEST_ITEMS } from './quests/regions.js';
import { ClassIdSchema } from './classes.js';
import { BIOME_SET_TEMPLATES } from './biome-sets.js';
import { QUEST_GEAR_TEMPLATES } from './quest-gear.js';
import { RAID_GEAR_TEMPLATES } from './raid-gear.js';
import { RARE_TROPHY_TEMPLATES } from './rare-mobs.js';
import { COSMETIC_TEMPLATES } from './cosmetics/index.js';
import { TRAILS } from './trails.js';
import { UPGRADE_MATERIAL_TEMPLATES } from './upgrade-materials.js';
/**
 * Item templates: the static definition of every object in the game
 * (CADRAGE, "Équipement et inventaire"). An instance in a bag or on a body
 * points at one of these by id and adds only what varies: owner, quantity.
 *
 * Visible equipment carries a `sprite` key (an overlay sheet in the manifest)
 * and a `tint`: one shape, several items (CLAUDE.md, "Teinte").
 */
export const RARITIES = ['common', 'rare', 'epic', 'legendary'];
export const RaritySchema = z.enum(RARITIES);
/** Higher is rarer; for sorting and colour. */
export function rarityRank(rarity) {
    return RARITIES.indexOf(rarity);
}
/** Display colour of a rarity, shared by tooltips, names on the ground and loot text. */
export const RARITY_COLORS = {
    common: 0xe6e6f0,
    rare: 0x5aa9ff,
    epic: 0xc678ff,
    legendary: 0xffb347,
};
/**
 * Equipment slots. No jewellery (ADR 0015): a ring or an amulet is a pixel on
 * the character and only split the stats further, so every piece worn is one
 * that shows.
 */
export const EQUIP_SLOTS = [
    'helmet',
    'chest',
    'gloves',
    'boots',
    'weapon',
    'offhand',
    /** A companion out beside the character, or under them; see `data/companions`. */
    'pet',
    'mount',
];
export const EquipSlotSchema = z.enum(EQUIP_SLOTS);
/**
 * What a piece of equipment may be: an equipment slot's, wings (ADR 0053)
 * or a footstep trail (ADR 0073). Wings and trails are a look and nothing
 * else, so they have no equipment place of their own, only the cosmetic
 * places `look-wings` and `look-trail`.
 */
export const ITEM_SLOTS = [...EQUIP_SLOTS, 'wings', 'trail'];
export const ItemSlotSchema = z.enum(ITEM_SLOTS);
/** Slots a piece is only ever a cosmetic of: no equipment place under it. */
export function isLookOnlySlot(slot) {
    return slot === 'wings' || slot === 'trail';
}
/**
 * The cosmetic places (ADR 0031): one over each drawn slot, where a piece
 * bought for its look is worn *on top of* the real gear. What is drawn is
 * the cosmetic; what counts in a fight is the piece under it. A companion
 * has no cosmetic place: a pet or a mount already is only its look.
 */
export const COSMETIC_SLOTS = [
    'look-helmet',
    'look-chest',
    'look-gloves',
    'look-boots',
    'look-weapon',
    'look-offhand',
    'look-wings',
    'look-trail',
];
export const CosmeticSlotSchema = z.enum(COSMETIC_SLOTS);
/** The slot a cosmetic place covers: `look-helmet` → `helmet`, `look-wings` → `wings`. */
export function coveredSlot(slot) {
    return slot.slice('look-'.length);
}
/** The cosmetic place over a slot, or undefined for a companion's. */
export function cosmeticSlotOf(slot) {
    const candidate = `look-${slot}`;
    return COSMETIC_SLOTS.includes(candidate)
        ? candidate
        : undefined;
}
/**
 * Where an instance is worn: one place per slot, the equipment's and, over
 * the drawn ones, the cosmetics'. Kept apart from the slot of a template so
 * a template's `slot` says what the piece *is* (a helmet) and its body slot
 * where it *goes* (the helmet's place, or the cosmetic place over it).
 */
export const BODY_SLOTS = [...EQUIP_SLOTS, ...COSMETIC_SLOTS];
export const BodySlotSchema = z.enum(BODY_SLOTS);
export function isCosmeticSlot(slot) {
    return COSMETIC_SLOTS.includes(slot);
}
export const ITEM_KINDS = ['equipment', 'consumable', 'material'];
const HEX_COLOR = /^#[0-9a-f]{6}$/;
const ITEM_ID = /^[a-z][a-z0-9-]*$/;
/**
 * What a piece gives (ADR 0015): damage per second, which every piece carries;
 * defence, which the armour carries (helmet, chest, off-hand, gloves, boots);
 * and health, which the helmet, the chest and the off-hand add on top of the
 * level's. One class, so no attribute to split a piece's worth between.
 */
export const StatBonusSchema = z
    .object({
    health: z.number().int().min(0).default(0),
    defence: z.number().int().min(0).default(0),
    /** In tenths: a helmet gives a third of what a sword does, and 0.3 must not round to 0. */
    dps: z
        .number()
        .min(0)
        .refine((value) => Math.abs(value * 10 - Math.round(value * 10)) < 1e-9, 'One decimal at most')
        .default(0),
})
    .default({ health: 0, defence: 0, dps: 0 });
export const ItemTemplateSchema = z
    .object({
    id: z.string().regex(ITEM_ID, 'kebab-case id'),
    /** Player-facing, French. */
    name: z.string().min(1).max(40),
    kind: z.enum(ITEM_KINDS),
    rarity: RaritySchema.default('common'),
    levelRequired: z.number().int().min(1).max(30).default(1),
    /** Empty means every class. */
    classes: z.array(ClassIdSchema).default([]),
    /** 1 for equipment; a stack of potions or materials goes higher. */
    maxStack: z.number().int().min(1).max(99).default(1),
    /** Equipment only. */
    slot: ItemSlotSchema.optional(),
    bonus: StatBonusSchema,
    /** Overlay sheet key in the manifest, for visible slots. */
    sprite: z.string().min(1).optional(),
    /** Tint applied to the greyscale overlay, and to the icon. */
    tint: z.string().regex(HEX_COLOR).optional(),
    /** Inventory icon key in the manifest. */
    icon: z.string().min(1),
    /** The wake a `trail` cosmetic leaves behind its wearer (`data/trails.ts`, ADR 0073). */
    trail: z.string().min(1).optional(),
    /** Companion this item summons when worn in the `pet` or `mount` slot (`data/companions`). */
    companion: z.string().min(1).optional(),
    /**
     * A piece worn for its look alone (ADR 0031): it goes to the cosmetic
     * place over its slot, is drawn instead of the real piece, and gives
     * nothing — so it can neither be found, upgraded nor fused.
     */
    cosmetic: z.boolean().default(false),
    /**
     * Whether it belongs to a biome's gear pool, which monsters, chests and
     * bosses draw from (`data/loot.ts`). A cosmetic never does: it is bought,
     * or it is one boss's named trophy (`BOSS_TROPHIES`, ADR 0053).
     */
    lootable: z.boolean().default(true),
    /**
     * Found outside the biomes' pools: `raid` is Shefira's set (ADR 0068),
     * rolled by its own profile (`rollRaidLoot`). Such a piece is not
     * `lootable`, yet carries affixes like found gear, and its stats and
     * affixes are measured at `RAID_POWER_LEVEL` (`powerLevelOf`).
     */
    source: z.enum(['raid', 'dev']).optional(),
    /** Gold a merchant pays for it. */
    sellPrice: z.number().int().min(0).default(0),
    /** Consumables: health restored on use. */
    heal: z.number().int().positive().optional(),
    /**
     * A quest's bait (ADR 0047): used by a den of `den`, it calls out a
     * `summons` for its user alone (`rules/quests.ts`, `baitRefusal`). Mob
     * ids as strings: the bestiary imports this table.
     */
    bait: z.object({ summons: z.string().min(1), den: z.string().min(1) }).optional(),
})
    .superRefine((item, ctx) => {
    if (item.kind === 'equipment') {
        if (item.slot === undefined) {
            ctx.addIssue({ code: 'custom', path: ['slot'], message: 'Equipment needs a slot' });
        }
        if (item.maxStack !== 1) {
            ctx.addIssue({ code: 'custom', path: ['maxStack'], message: 'Equipment does not stack' });
        }
        if (item.slot !== undefined && !isVisibleSlot(item.slot) && item.sprite !== undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['sprite'],
                message: `A ${item.slot} is never drawn and takes no sprite`,
            });
        }
        if (item.slot !== undefined && isAlwaysDrawnSlot(item.slot) && item.sprite === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['sprite'],
                message: `A ${item.slot} is drawn on the character and needs a sprite`,
            });
        }
    }
    else if (item.slot !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['slot'], message: 'Only equipment has a slot' });
    }
    if (item.bait !== undefined && (item.kind !== 'consumable' || item.heal !== undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['bait'],
            message: 'A bait is a consumable that heals nothing',
        });
    }
    if (item.heal !== undefined && item.kind !== 'consumable') {
        ctx.addIssue({ code: 'custom', path: ['heal'], message: 'Only a consumable heals' });
    }
    if (item.slot !== undefined && isLookOnlySlot(item.slot) && !item.cosmetic) {
        ctx.addIssue({
            code: 'custom',
            path: ['cosmetic'],
            message: `A ${item.slot} piece is a look only: it must be cosmetic`,
        });
    }
    const isTrail = item.slot === 'trail';
    if (isTrail && (item.trail === undefined || TRAILS[item.trail] === undefined)) {
        ctx.addIssue({ code: 'custom', path: ['trail'], message: 'A trail names a known wake' });
    }
    if (!isTrail && item.trail !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['trail'], message: 'Only a trail leaves a wake' });
    }
    if (item.cosmetic) {
        const drawn = item.slot !== undefined && (isVisibleSlot(item.slot) || isTrail);
        if (item.kind !== 'equipment' || !drawn) {
            ctx.addIssue({
                code: 'custom',
                path: ['cosmetic'],
                message: 'A cosmetic is equipment of a drawn slot',
            });
        }
        // A trail is drawn from code, behind the feet: nothing on the body.
        if (item.sprite === undefined && !isTrail) {
            ctx.addIssue({
                code: 'custom',
                path: ['sprite'],
                message: 'A cosmetic is its look: it needs a sprite',
            });
        }
        if (Object.values(item.bonus).some((value) => value !== 0)) {
            ctx.addIssue({ code: 'custom', path: ['bonus'], message: 'A cosmetic gives no stat' });
        }
        if (item.lootable) {
            ctx.addIssue({
                code: 'custom',
                path: ['lootable'],
                message: 'A cosmetic is bought, never found',
            });
        }
    }
    if (item.source !== undefined && (item.kind !== 'equipment' || item.lootable)) {
        ctx.addIssue({
            code: 'custom',
            path: ['source'],
            message: 'A piece of a source of its own is equipment outside the biomes’ pools',
        });
    }
    const summons = item.slot === 'pet' || item.slot === 'mount';
    if (summons && item.companion === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['companion'],
            message: 'A pet or a mount names its companion',
        });
    }
    if (!summons && item.companion !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['companion'],
            message: 'Only a pet or a mount summons',
        });
    }
});
/**
 * Hand-made items: the starting kits, the dragon set, the cosmetics, the
 * consumables and the materials. The equipment found in the world is the
 * biome sets' (`biome-sets.ts`) and the smith's materials are
 * `upgrade-materials.ts`, both appended below.
 */
const DEFINITIONS = [
    // Starting kits, one per class: given at creation, never found.
    {
        id: 'sword-short',
        lootable: false,
        name: 'Épée courte',
        kind: 'equipment',
        slot: 'weapon',
        classes: ['warrior'],
        bonus: { dps: 2 },
        sprite: 'overlay.sword-short',
        icon: 'icon.sword-short',
        sellPrice: 5,
    },
    {
        id: 'bow-short',
        lootable: false,
        name: 'Arc court',
        kind: 'equipment',
        slot: 'weapon',
        classes: ['archer'],
        bonus: { dps: 2 },
        sprite: 'overlay.bow-short',
        icon: 'icon.bow-short',
        sellPrice: 5,
    },
    {
        id: 'wand-wooden',
        lootable: false,
        name: 'Baguette en bois',
        kind: 'equipment',
        slot: 'weapon',
        classes: ['mage'],
        bonus: { dps: 2 },
        sprite: 'overlay.wand-wooden',
        icon: 'icon.wand-wooden',
        sellPrice: 5,
    },
    // A unique weapon: its own drawing, derived from its icon (pnpm build:item),
    // so it carries its own colours and no tint.
    {
        id: 'dragon-sword',
        lootable: false,
        levelRequired: 10,
        name: 'Épée du dragon',
        kind: 'equipment',
        slot: 'weapon',
        rarity: 'epic',
        classes: ['warrior'],
        bonus: { dps: 8 },
        sprite: 'overlay.dragon-sword',
        icon: 'icon.dragon-sword',
        sellPrice: 120,
    },
    // The dragon set: its own drawings, kept apart from the biome sets
    // (`biome-sets.ts`) and out of the loot, for a dungeon boss to come.
    {
        id: 'dragon-helm',
        lootable: false,
        levelRequired: 10,
        name: 'Heaume du dragon',
        kind: 'equipment',
        slot: 'helmet',
        rarity: 'epic',
        bonus: { dps: 1.4, defence: 11, health: 39 },
        sprite: 'overlay.dragon-helm',
        icon: 'icon.dragon-helm',
        sellPrice: 90,
    },
    {
        id: 'dragon-chest',
        lootable: false,
        levelRequired: 10,
        name: 'Plastron du dragon',
        kind: 'equipment',
        slot: 'chest',
        rarity: 'epic',
        bonus: { dps: 2.1, defence: 14, health: 52 },
        sprite: 'overlay.dragon-chest',
        icon: 'icon.dragon-chest',
        sellPrice: 140,
    },
    {
        id: 'dragon-gloves',
        lootable: false,
        levelRequired: 10,
        name: 'Gantelets du dragon',
        kind: 'equipment',
        slot: 'gloves',
        rarity: 'epic',
        bonus: { dps: 2.1, defence: 7 },
        sprite: 'overlay.dragon-gloves',
        icon: 'icon.dragon-gloves',
        sellPrice: 70,
    },
    {
        id: 'dragon-boots',
        lootable: false,
        levelRequired: 10,
        name: 'Bottes du dragon',
        kind: 'equipment',
        slot: 'boots',
        rarity: 'epic',
        bonus: { dps: 1.4, defence: 7 },
        sprite: 'overlay.dragon-boots',
        icon: 'icon.dragon-boots',
        sellPrice: 70,
    },
    {
        id: 'dragon-shield',
        lootable: false,
        levelRequired: 10,
        name: 'Bouclier du dragon',
        kind: 'equipment',
        slot: 'offhand',
        rarity: 'epic',
        classes: ['warrior'],
        bonus: { dps: 1.4, defence: 11, health: 42 },
        sprite: 'overlay.dragon-shield',
        icon: 'icon.dragon-shield',
        sellPrice: 110,
    },
    // The founder pack (ADR 0031), sold in the shop for real money and worn
    // for its look: the sword is a cosmetic over the real weapon, animated
    // (four frames of its own in the rig, cycling on the body's frame index),
    // the companions are only the creature that appears. No sell price: what
    // was paid for never turns into gold.
    {
        id: 'flame-sword',
        lootable: false,
        cosmetic: true,
        name: 'Épée de flamme',
        kind: 'equipment',
        slot: 'weapon',
        rarity: 'legendary',
        sprite: 'overlay.flame-sword',
        icon: 'icon.flame-sword',
    },
    // The rest of the fire set (ADR 0031): worn pieces drawn on the body and
    // animated (four frames of their own in the rig), the shield a held piece
    // derived from its animated icon. Cosmetics all: looks over the real gear.
    {
        id: 'flame-helm',
        lootable: false,
        cosmetic: true,
        name: 'Heaume de flamme',
        kind: 'equipment',
        slot: 'helmet',
        rarity: 'legendary',
        sprite: 'overlay.flame-helm',
        icon: 'icon.flame-helm',
    },
    {
        id: 'flame-chest',
        lootable: false,
        cosmetic: true,
        name: 'Cuirasse de flamme',
        kind: 'equipment',
        slot: 'chest',
        rarity: 'legendary',
        sprite: 'overlay.flame-chest',
        icon: 'icon.flame-chest',
    },
    {
        id: 'flame-gloves',
        lootable: false,
        cosmetic: true,
        name: 'Gantelets de flamme',
        kind: 'equipment',
        slot: 'gloves',
        rarity: 'legendary',
        sprite: 'overlay.flame-gloves',
        icon: 'icon.flame-gloves',
    },
    {
        id: 'flame-boots',
        lootable: false,
        cosmetic: true,
        name: 'Bottes de flamme',
        kind: 'equipment',
        slot: 'boots',
        rarity: 'legendary',
        sprite: 'overlay.flame-boots',
        icon: 'icon.flame-boots',
    },
    {
        id: 'flame-shield',
        lootable: false,
        cosmetic: true,
        name: 'Bouclier de flamme',
        kind: 'equipment',
        slot: 'offhand',
        rarity: 'legendary',
        classes: ['warrior'],
        sprite: 'overlay.flame-shield',
        icon: 'icon.flame-shield',
    },
    // Wings (ADR 0053): a look only, on its own place over no equipment. Drawn
    // on the body behind the back and animated (four frames in the rig), like
    // the fire set's worn pieces. Not sold: the frost giant's rare trophy
    // (`BOSS_TROPHIES`), so not in the biome's pool either.
    {
        id: 'ice-wings',
        lootable: false,
        cosmetic: true,
        name: 'Ailes de glace',
        kind: 'equipment',
        slot: 'wings',
        rarity: 'legendary',
        sprite: 'overlay.ice-wings',
        icon: 'icon.ice-wings',
    },
    // The founder pack's wings, in the fire set's obsidian, lava and flames.
    {
        id: 'flame-wings',
        lootable: false,
        cosmetic: true,
        name: 'Ailes de flamme',
        kind: 'equipment',
        slot: 'wings',
        rarity: 'legendary',
        sprite: 'overlay.flame-wings',
        icon: 'icon.flame-wings',
    },
    // The dragon pack's wings, in the dragon set's crimson and gold.
    {
        id: 'dragon-wings',
        lootable: false,
        cosmetic: true,
        name: 'Ailes du dragon',
        kind: 'equipment',
        slot: 'wings',
        rarity: 'epic',
        sprite: 'overlay.dragon-wings',
        icon: 'icon.dragon-wings',
    },
    // Footstep trails (ADR 0073), sold in the shop: a wake drawn from code
    // behind the feet (`data/trails.ts`), so no sprite, only an icon.
    {
        id: 'trail-embers',
        lootable: false,
        cosmetic: true,
        name: 'Traînée de braises',
        kind: 'equipment',
        slot: 'trail',
        rarity: 'epic',
        trail: 'embers',
        icon: 'icon.trail-embers',
    },
    {
        id: 'trail-frost',
        lootable: false,
        cosmetic: true,
        name: 'Traînée de givre',
        kind: 'equipment',
        slot: 'trail',
        rarity: 'epic',
        trail: 'frost',
        icon: 'icon.trail-frost',
    },
    {
        id: 'trail-petals',
        lootable: false,
        cosmetic: true,
        name: 'Traînée de pétales',
        kind: 'equipment',
        slot: 'trail',
        rarity: 'rare',
        trail: 'petals',
        icon: 'icon.trail-petals',
    },
    {
        id: 'trail-leaves',
        lootable: false,
        cosmetic: true,
        name: 'Traînée de feuilles',
        kind: 'equipment',
        slot: 'trail',
        rarity: 'rare',
        trail: 'leaves',
        icon: 'icon.trail-leaves',
    },
    // A game master's test blade: found nowhere, sold nowhere, worth nothing;
    // only an admin grant (F3, `db:grant`) makes one, to try Shefira's raid alone.
    {
        id: 'gm-blade',
        lootable: false,
        name: 'Lame du Maître du jeu',
        kind: 'equipment',
        slot: 'weapon',
        rarity: 'legendary',
        classes: ['warrior'],
        bonus: { dps: 400 },
        source: 'dev',
        sprite: 'overlay.givrecoeur-oath',
        icon: 'icon.givrecoeur-oath',
        sellPrice: 0,
    },
    // Reward for completing every biome's achievements (docs/adr/0046-achievements-and-titles.md), never sold.
    {
        id: 'conqueror-blade',
        lootable: false,
        cosmetic: true,
        name: 'Lame des Cinq Terres',
        kind: 'equipment',
        slot: 'weapon',
        rarity: 'legendary',
        sprite: 'overlay.conqueror-blade',
        icon: 'icon.conqueror-blade',
    },
    {
        id: 'fire-hound',
        lootable: false,
        name: 'Chien de feu',
        kind: 'equipment',
        slot: 'pet',
        rarity: 'legendary',
        companion: 'fire-hound',
        icon: 'icon.fire-hound',
    },
    {
        // The seventh day of the account's streak (ADR 0051): never sold nor found, bound.
        id: 'light-fox',
        lootable: false,
        name: 'Renard de lumière',
        kind: 'equipment',
        slot: 'pet',
        rarity: 'legendary',
        companion: 'light-fox',
        icon: 'icon.light-fox',
    },
    {
        // The beta testers' thanks (ADR 0083), claimed from their achievement: never sold nor found, bound.
        id: 'glitch',
        lootable: false,
        name: 'Glitch',
        kind: 'equipment',
        slot: 'pet',
        rarity: 'legendary',
        companion: 'glitch',
        icon: 'icon.glitch',
    },
    {
        // Earned, never bought (ADR 0047): the forest chain's last reward, bound.
        id: 'forest-fox-cub',
        lootable: false,
        name: 'Renardeau de Sylvebois',
        kind: 'equipment',
        slot: 'pet',
        rarity: 'rare',
        companion: 'forest-fox-cub',
        icon: 'icon.forest-fox-cub',
    },
    {
        // Earned, never bought (ADR 0047): the swamp chain's last reward, bound.
        id: 'swamp-caiman',
        lootable: false,
        name: 'Petit caïman de Brumevase',
        kind: 'equipment',
        slot: 'pet',
        rarity: 'rare',
        companion: 'swamp-caiman',
        icon: 'icon.swamp-caiman',
    },
    {
        id: 'fire-horse',
        lootable: false,
        name: 'Cheval de feu',
        kind: 'equipment',
        slot: 'mount',
        rarity: 'legendary',
        companion: 'fire-horse',
        icon: 'icon.fire-horse',
    },
    {
        // Earned, never bought (ADR 0047): the meadow chain's last reward, bound.
        id: 'meadow-horse',
        lootable: false,
        name: 'Cheval de Havrebourg',
        kind: 'equipment',
        slot: 'mount',
        rarity: 'rare',
        companion: 'meadow-horse',
        icon: 'icon.meadow-horse',
    },
    {
        // Earned, never bought (ADR 0047): the camel drivers' reward in the desert, bound.
        id: 'desert-camel',
        lootable: false,
        name: 'Chameau de Sablerouge',
        kind: 'equipment',
        slot: 'mount',
        rarity: 'epic',
        companion: 'desert-camel',
        icon: 'icon.desert-camel',
    },
    {
        id: 'pegasus',
        lootable: false,
        name: 'Pégase',
        kind: 'equipment',
        slot: 'mount',
        rarity: 'legendary',
        companion: 'pegasus',
        icon: 'icon.pegasus',
    },
    {
        // Earned, never bought (ADR 0068 §6): the raid quest's reward, bound.
        id: 'shefira-drake',
        lootable: false,
        name: 'Drake de Shefira',
        kind: 'equipment',
        slot: 'mount',
        rarity: 'legendary',
        companion: 'shefira-drake',
        icon: 'icon.shefira-drake',
    },
    // The dragon set's look (ADR 0031), sold in the shop: the very drawings of
    // the dragon pieces above, worn over the real gear without their stats.
    // « (apparence) » in the name keeps a bag that holds both apart.
    {
        id: 'dragon-sword-look',
        lootable: false,
        cosmetic: true,
        name: 'Épée du dragon (apparence)',
        kind: 'equipment',
        slot: 'weapon',
        rarity: 'epic',
        classes: ['warrior'],
        sprite: 'overlay.dragon-sword',
        icon: 'icon.dragon-sword',
    },
    {
        id: 'dragon-helm-look',
        lootable: false,
        cosmetic: true,
        name: 'Heaume du dragon (apparence)',
        kind: 'equipment',
        slot: 'helmet',
        rarity: 'epic',
        sprite: 'overlay.dragon-helm',
        icon: 'icon.dragon-helm',
    },
    {
        id: 'dragon-chest-look',
        lootable: false,
        cosmetic: true,
        name: 'Plastron du dragon (apparence)',
        kind: 'equipment',
        slot: 'chest',
        rarity: 'epic',
        sprite: 'overlay.dragon-chest',
        icon: 'icon.dragon-chest',
    },
    {
        id: 'dragon-gloves-look',
        lootable: false,
        cosmetic: true,
        name: 'Gantelets du dragon (apparence)',
        kind: 'equipment',
        slot: 'gloves',
        rarity: 'epic',
        sprite: 'overlay.dragon-gloves',
        icon: 'icon.dragon-gloves',
    },
    {
        id: 'dragon-boots-look',
        lootable: false,
        cosmetic: true,
        name: 'Bottes du dragon (apparence)',
        kind: 'equipment',
        slot: 'boots',
        rarity: 'epic',
        sprite: 'overlay.dragon-boots',
        icon: 'icon.dragon-boots',
    },
    {
        id: 'dragon-shield-look',
        lootable: false,
        cosmetic: true,
        name: 'Bouclier du dragon (apparence)',
        kind: 'equipment',
        slot: 'offhand',
        rarity: 'epic',
        classes: ['warrior'],
        sprite: 'overlay.dragon-shield',
        icon: 'icon.dragon-shield',
    },
    // Off-hands, one per class.
    {
        id: 'shield-wooden',
        lootable: false,
        name: 'Bouclier en bois',
        kind: 'equipment',
        slot: 'offhand',
        classes: ['warrior'],
        bonus: { dps: 0.2, defence: 2, health: 10 },
        sprite: 'overlay.shield-round',
        icon: 'icon.shield-round',
        sellPrice: 4,
    },
    {
        id: 'quiver-leather',
        lootable: false,
        name: 'Carquois en cuir',
        kind: 'equipment',
        slot: 'offhand',
        classes: ['archer'],
        bonus: { dps: 0.2 },
        sprite: 'overlay.quiver',
        icon: 'icon.quiver',
        sellPrice: 4,
    },
    {
        id: 'grimoire-worn',
        lootable: false,
        name: 'Grimoire usé',
        kind: 'equipment',
        slot: 'offhand',
        classes: ['mage'],
        bonus: { dps: 0.2 },
        sprite: 'overlay.grimoire',
        icon: 'icon.grimoire',
        sellPrice: 4,
    },
    // Consumables.
    {
        id: 'potion-small',
        name: 'Petite potion de vie',
        kind: 'consumable',
        maxStack: 20,
        heal: 30,
        icon: 'icon.potion',
        tint: '#ff5c5c',
        sellPrice: 2,
    },
    {
        id: 'potion-medium',
        name: 'Potion de vie',
        kind: 'consumable',
        rarity: 'rare',
        levelRequired: 5,
        maxStack: 20,
        heal: 80,
        icon: 'icon.potion',
        tint: '#ff2f6d',
        sellPrice: 8,
    },
    {
        id: 'bread',
        name: 'Pain',
        kind: 'consumable',
        maxStack: 10,
        heal: 15,
        icon: 'icon.bread',
        sellPrice: 1,
    },
    // Quest items (ADR 0047): given by a quest, bound, worth nothing to a merchant.
    {
        id: 'plague-bait',
        name: 'Appât pestilentiel',
        kind: 'consumable',
        maxStack: 1,
        icon: 'icon.plague-bait',
        lootable: false,
        bait: { summons: 'plague-rat', den: 'rat' },
    },
    // Materials: what a rat leaves behind.
    {
        id: 'rat-tail',
        name: 'Queue de rat',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.rat-tail',
        sellPrice: 1,
    },
    {
        id: 'rat-pelt',
        name: 'Peau de rat',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.rat-pelt',
        sellPrice: 2,
    },
    {
        id: 'boar-tusk',
        name: 'Défense de sanglier',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.boar-tusk',
        sellPrice: 3,
    },
    {
        id: 'boar-hide',
        name: 'Cuir de sanglier',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.boar-hide',
        // 5 before the meadow's quests paid gold of their own (ADR 0047).
        sellPrice: 4,
    },
    {
        id: 'wolf-pelt',
        name: 'Peau de loup',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.wolf-pelt',
        sellPrice: 4,
    },
    {
        id: 'goblin-ear',
        name: 'Oreille de gobelin',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.goblin-ear',
        sellPrice: 4,
    },
    {
        id: 'toad-slime',
        name: 'Bave de crapaud',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.toad-slime',
        sellPrice: 6,
    },
    {
        id: 'lizard-scale',
        name: 'Écaille de lézard',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.lizard-scale',
        sellPrice: 7,
    },
    {
        id: 'hyena-fang',
        name: 'Croc de hyène',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.hyena-fang',
        sellPrice: 8,
    },
    {
        id: 'mummy-wrap',
        name: 'Bandelette de momie',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.mummy-wrap',
        sellPrice: 10,
    },
    {
        id: 'bear-fur',
        name: "Fourrure d'ours",
        kind: 'material',
        maxStack: 50,
        icon: 'icon.bear-fur',
        sellPrice: 12,
    },
    {
        id: 'yeti-horn',
        name: 'Corne de yéti',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.yeti-horn',
        sellPrice: 15,
    },
    // The second bestiary (2026-09-27): one material per creature, priced
    // between its neighbours' along the level curve.
    {
        id: 'fox-tail',
        name: 'Queue de renard',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.fox-tail',
        sellPrice: 2,
    },
    {
        id: 'treant-bark',
        name: 'Écorce de sylvain',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.treant-bark',
        sellPrice: 5,
    },
    {
        id: 'caiman-tooth',
        name: 'Dent de caïman',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.caiman-tooth',
        sellPrice: 8,
    },
    {
        id: 'scorpion-stinger',
        name: 'Dard de scorpion',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.scorpion-stinger',
        sellPrice: 9,
    },
    {
        id: 'ember-scale',
        name: 'Écaille de braise',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.ember-scale',
        sellPrice: 11,
    },
    {
        id: 'ice-wolf-fang',
        name: 'Croc de givre',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.ice-wolf-fang',
        sellPrice: 12,
    },
    {
        id: 'frozen-heart',
        name: 'Cœur gelé',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.frozen-heart',
        sellPrice: 14,
    },
    {
        id: 'goblin-fletching',
        name: 'Empennage gobelin',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.goblin-fletching',
        sellPrice: 5,
    },
    {
        id: 'wisp-glow',
        name: 'Lueur de feu follet',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.wisp-glow',
        sellPrice: 7,
    },
    {
        id: 'cobra-venom',
        name: 'Venin de cobra',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.cobra-venom',
        sellPrice: 9,
    },
    {
        id: 'harpy-feather',
        name: 'Plume de harpie',
        kind: 'material',
        maxStack: 50,
        icon: 'icon.harpy-feather',
        sellPrice: 13,
    },
];
/** Parsed once at module load: a malformed table fails the server at boot. */
export const ITEMS = new Map([
    ...DEFINITIONS,
    ...BIOME_SET_TEMPLATES,
    ...QUEST_GEAR_TEMPLATES,
    ...RAID_GEAR_TEMPLATES,
    ...COSMETIC_TEMPLATES,
    ...UPGRADE_MATERIAL_TEMPLATES,
    ...ALCHEMY_POTION_TEMPLATES,
    // The wandering rares' trophies (ADR 0094, `rare-mobs.ts`).
    ...RARE_TROPHY_TEMPLATES,
    // The regions' quest items (ADR 0047, `quests/<biome>.ts`).
    ...QUEST_ITEMS,
].map((definition) => {
    const item = ItemTemplateSchema.parse(definition);
    return [item.id, item];
}));
export function getItem(id) {
    return ITEMS.get(id);
}
/**
 * Where a template is worn: its slot, or for a cosmetic the cosmetic place
 * over it. Undefined for anything that is not equipment.
 */
export function bodySlotOf(template) {
    if (template.kind !== 'equipment' || template.slot === undefined)
        return undefined;
    if (template.cosmetic)
        return cosmeticSlotOf(template.slot);
    // A look-only slot has no equipment place: the schema refuses such a piece.
    return template.slot === 'wings' || template.slot === 'trail' ? undefined : template.slot;
}
/** The instance side: what varies per object. Sent to its owner only. */
export const ItemInstanceSchema = z.object({
    id: z.uuid(),
    templateId: z.string().regex(ITEM_ID),
    quantity: z.number().int().min(1).max(99),
    /**
     * Smith's level, +0 to +10 (ADR 0013; the ceiling is `MAX_UPGRADE_LEVEL`,
     * written out here because `upgrades.ts` imports this file).
     */
    upgradeLevel: z.number().int().min(0).max(10).default(0),
    /** Bound to its character (reached +4): no trade, no auction, merchants only. */
    bound: z.boolean().default(false),
    /**
     * ISO 8601: a shop purchase (ADR 0044) is neither traded nor auctioned
     * before this moment. Absent for everything else.
     */
    tradableAfter: z.iso.datetime({ offset: true }).optional(),
    /**
     * Rolled once when the piece is made, rerolled at the runesmith (ADR
     * 0057). Absent on everything that carries none.
     */
    affixes: AffixListSchema.optional(),
});
//# sourceMappingURL=items.js.map